// Partner visits API (2026-10-06): see docs/superpowers/specs/2026-10-06-partner-visits-api-design.md

import { observable, configure, action, computed } from 'mobx';
import LocalModel from './localModel';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';
import firebaseSync from '../firebaseSync';
import offlineRetryQueue from '../lib/offlineRetryQueue';
import * as offlineWriteQueue from '../lib/offlineWriteQueue';
import { isNetworkErrorEnvelope } from '../lib/offlineCache';
import { diffJournalForSave } from '../lib/journalSaveDiff';
import * as journalDraft from '../lib/journalDraft';
import { remapObjectTempId, isResolvableTempId } from '../lib/offlineTempIdRemap';
import { partnerStoreMethods } from '../lib/partnerSuggestions';

configure({ enforceActions: 'always' });

class JournalStore extends LocalModel {
    constructor() {
        super('journal', {
            namePlural: 'journals',
            sort: '-date -createdDate',
            limit: 500,
            api: {
                search: {
                    url: '/api/journals/',
                    params: {
                        limit: 15,
                        sort: '-date -createdDate',
                    },
                },
                load: {
                    url: '/api/journals/',
                    params: {},
                },
                save: {
                    url: '/api/journals/',
                    params: {},
                },
                delete: {
                    url: '/api/journals/',
                    params: {},
                },
            },
        });
    }

    async saveField(id, field, value, updateMemory, opt = {}) {
        const response = await super.saveField(id, field, value, updateMemory, opt);
        if (response && response.message && response.message.indexOf('ERROR fetching API') !== -1) {
            offlineRetryQueue.addToQueue('/api/journals/', id, field, value);
        }
        // After saving date, reload journal to pick up backend medicine date cascade
        if (field === 'date' && value && response?.status < 300) {
            const reloaded = await this.load(parseInt(id, 10), false, { skipUpdate: true });
            if (reloaded && reloaded[0]) {
                this.updateKeyValue('newJournal', reloaded[0]);
                this.setNewJournalBaseline(reloaded[0]);
                // Also update in animalJournals array
                const ajIdx = this.animalJournals?.findIndex(j => j.id === parseInt(id, 10));
                if (ajIdx > -1) {
                    const updated = [...this.animalJournals];
                    updated[ajIdx] = reloaded[0];
                    this.updateKeyValue('animalJournals', updated);
                }
                // Also update in journals array
                const jIdx = this.journals?.findIndex(j => j.id === parseInt(id, 10));
                if (jIdx > -1) {
                    const updated = [...this.journals];
                    updated[jIdx] = reloaded[0];
                    this.updateKeyValue('journals', updated);
                }
            }
        }
        return response;
    }

    // Offline-aware POST. See VisitorStore.insert for rationale. Body refs
    // (`animal`, `sale`, `visitor`) may be tempIds — drain rewrites them.
    async insert(data, event) {
        const response = await super.insert(data, event);
        if (isNetworkErrorEnvelope(response)) {
            const tempId = await offlineWriteQueue.enqueueInsert({
                type: 'journal',
                url: '/api/journals/',
                body: data,
            });
            if (tempId == null) return response;
            return {
                status: 201,
                data: { journal: { ...data, id: tempId, _pendingSync: true } },
            };
        }
        return response;
    }

    @observable newJournal = {};

    // Plain (non-observable) snapshot of newJournal as last hydrated from the server.
    // saveJournalDiff() diffs against this so full-journal saves only send fields that
    // actually changed in THIS session — a stale copy can then never overwrite text
    // another user/window saved in the meantime. See lib/journalSaveDiff.js (TICKET-01303).
    newJournalBaseline = null;

    setNewJournalBaseline(journal) {
        this.newJournalBaseline = journal ? JSON.parse(JSON.stringify(journal)) : null;
    }

    // Send an already-diffed journal payload. Skips the PATCH entirely when nothing
    // changed (a no-op would just bump updatedDate and create a version doc), and
    // advances the baseline on success so the next diff is relative to what the server
    // now has from this session.
    async savePreparedJournal(payload) {
        const keys = Object.keys(payload || {});
        if (keys.length === 1 && keys[0] === 'id') {
            return { status: 200, message: 'No changes to save' };
        }
        const response = await this.save(payload);
        if (response?.status < 300) {
            const base = (this.newJournalBaseline && this.newJournalBaseline.id === payload.id)
                ? this.newJournalBaseline : {};
            this.newJournalBaseline = { ...base, ...JSON.parse(JSON.stringify(payload)) };
        }
        return response;
    }

    async saveJournalDiff(journalInput) {
        return this.savePreparedJournal(diffJournalForSave(journalInput, this.newJournalBaseline));
    }

    @observable journal = {};

    @observable journals = [];

    @observable editJournals = [];

    @observable allJournals = [];

    @observable visitorJournals = [];

    @observable animalJournals = [];

    @observable allAnimalJournals = [];

    @observable reportingToDyreId = false;
    @observable dyreIdReportError = null;
    @observable reportingBatchToDyreId = false;
    @observable dyreIdBatchReportError = null;
    @observable dyreIdBatchResults = null;

    @observable journalsToSend = [];

    @observable veterinaries = [];

    @observable animals = [];

    @observable visitors = [];

    @observable owners = [];

    @observable sales = [];

    @observable deletedSales = [];

    @observable visitorSales = [];

    @observable medicineUsage = [];

    // Set cutoffDate to 1. of current month.
    // @observable cutoffDate = util.isoDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1), false, false, true);
    @observable cutoffDate = null;

    // Set cutoffDateEnd to last day of current month.
    // @observable cutoffDateEnd = util.isoDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0), false, false, true);
    @observable cutoffDateEnd = null;

    @observable searchText = null;
    @observable currentPage = 1;
    @observable activeStatus = null;
    @observable activeMode = null;
    @observable veterinary = null;
    @observable animalSpeciesId = null;
    @observable animalSpeciesList = [];
    @observable limit = 50;
    @observable sort = '-date -createdDate';
    @observable offset = 0;
    @observable currentPage = 1;

    @observable templateJournals = [];

    @observable previousAnimalDiagnoses = [];

    @observable aiResponse = {};
    @observable aiResponseObject = {};

    // Partner (AI scribe) suggestions — see lib/partnerSuggestions.js
    @observable partnerLaunchers = [];
    @observable partnerSuggestionIndex = {};
    @observable partnerPending = [];
    partnerWatcherDisposer = null;

    @action
    setSortField(field) {
        this.sort = field;
    }

    /**
     * newJournal.visitor / .animal / .sale are captured from the sale the moment
     * the journal drawer opens (newJournal.js loadJournal). If the customer was
     * created offline, that capture is a temp id — and it stays one for the whole
     * time the journal is open, so the journal is saved against a customer that
     * does not exist. Swap it as soon as the queue resolves the real id.
     */
    @action
    replaceOfflineTempId(tempId, realId) {
        if (!isResolvableTempId(tempId, realId)) return;
        const newJournal = remapObjectTempId(this.newJournal, ['visitor', 'animal', 'sale'], tempId, realId);
        if (newJournal !== this.newJournal) {
            this.updateKeyValue('newJournal', newJournal);
        }
        const journal = remapObjectTempId(this.journal, ['visitor', 'animal', 'sale'], tempId, realId);
        if (journal !== this.journal) {
            this.updateKeyValue('journal', journal);
        }
    }

    @action
    clearAll() {
        this.rescueNewJournalDraft();
        this.newJournal = {};
        this.journal = {};
        this.journals = [];
        this.editJournals = [];
        this.allJournals = [];
        this.visitorJournals = [];
        this.animalJournals = [];
        this.allAnimalJournals = [];
        this.journalsToSend = [];
        this.templateJournals = [];
        this.animals = [];
        this.visitors = [];
        this.owners = [];
        this.sales = [];
        this.visitorSales = [];
    }

    async searchSpecies(search) {
        if (!search || search.length < 2) {
            return [];
        }
        const response = await util.fetchApi('/api/animalspecies/', { method: 'GET' }, {
            search,
            limit: 25,
        });
        if (response?.status === 200) {
            return response.data || [];
        }
        return [];
    }

    async searchDiagnoses(search) {
        if (!search || search.length < 2) {
            return [];
        }
        const response = await util.fetchApi('/api/diagnoses/', { method: 'GET' }, {
            search,
            limit: 25,
        });
        if (response?.status === 200) {
            return response.data || [];
        }
        return [];
    }

    async submitFeedback(journalId, feedback, rating) {
        return util.fetchApi(`/api/journals/${journalId}/feedback`, { method: 'POST' }, {
            feedback,
            rating,
        });
    }

    async loadTemplates({ speciesId, diagnoseCodes } = {}) {
        const params = {
            isTemplate: 'true',
            limit: 100,
            sort: '-createdDate',
        };
        if (speciesId) {
            params.speciesId = speciesId;
        }
        if (Array.isArray(diagnoseCodes) && diagnoseCodes.length > 0) {
            params.diagnoseCode = diagnoseCodes;
        }
        const response = await util.fetchApi('/api/journals/', { method: 'GET' }, params);
        if (response?.status === 200) {
            this.updateKeyValue('templateJournals', response.data || []);
        }
        return response;
    }

    // Diagnoses previously used on an animal, deduped by diagnose code (most
    // recent use wins), newest first. Used by the addDiagnose drawer so a vet
    // can re-use the exact same diagnose code as on an earlier journal.
    async loadPreviousAnimalDiagnoses(animalId, excludeJournalId) {
        this.updateKeyValue('previousAnimalDiagnoses', []);
        if (!animalId) {
            return [];
        }
        const response = await util.fetchApi('/api/journals/', { method: 'GET' }, {
            animal: animalId,
            limit: 100,
            sort: '-date -createdDate',
        });
        if (response?.status !== 200) {
            return [];
        }
        const journals = response.data || [];
        const byCode = new Map();
        journals.forEach(j => {
            if (excludeJournalId && j.id === excludeJournalId) {
                return;
            }
            (j.diagnoses || []).forEach(d => {
                // The add-to-journal flow requires a diagnose catalog id.
                if (!d || !d.id) {
                    return;
                }
                const key = d.diagnoseCode || `name:${d.name}`;
                const lastUsedDate = d.date || j.date;
                const existing = byCode.get(key);
                if (!existing || new Date(lastUsedDate) > new Date(existing.lastUsedDate)) {
                    byCode.set(key, {
                        id: d.id,
                        name: d.name,
                        diagnoseCode: d.diagnoseCode,
                        uuidv4: d.uuidv4,
                        speciesAllowed: d.speciesAllowed,
                        lastUsedDate,
                    });
                }
            });
        });
        const result = [...byCode.values()]
            .sort((a, b) => new Date(b.lastUsedDate) - new Date(a.lastUsedDate));
        this.updateKeyValue('previousAnimalDiagnoses', result);
        return result;
    }

    cleanupAi() {
        // A fresh built-in AI answer replaces the cards; drop the partner watcher,
        // index and banner list too so nothing stale is shown.
        this.clearPartnerSuggestions();
        this.updateKeyValue('aiResponse', {});
        this.updateKeyValue('aiResponseObject', {});
    }

    async queryAi(message) {
        this.cleanupAi();
        const response = await util.fetchApi(`/api/ai/journal`, { method: 'POST' }, {
            message,
        });
        switch (response?.status) {
            case 200:
                this.updateKeyValue('aiResponse', response?.data);
                let responseObject = {};
                if (response?.data?.output_text) {
                    try {
                        responseObject = JSON.parse(response?.data?.output_text);
                    } catch (e) {
                        // Not JSON, do nothing
                    }
                }
                if (responseObject) {
                    this.updateKeyValue('aiResponseObject', responseObject);
                }
                return response?.data;

        }
    }

    async queryAiFromConsultation(uuidv4) {
        this.cleanupAi();
        const response = await util.fetchApi(`/api/consultationrecordings/generate`, { method: 'POST' }, {
            uuidv4,
        });
        switch (response?.status) {
            case 200: {
                this.updateKeyValue('aiResponse', response?.data);
                let responseObject = {};
                if (response?.data?.output_text) {
                    try {
                        responseObject = JSON.parse(response?.data?.output_text);
                    } catch (e) {
                        // Not JSON, do nothing
                    }
                }
                if (responseObject) {
                    this.updateKeyValue('aiResponseObject', responseObject);
                }
                return response?.data;
            }

        }
    }

    // TICKET-01430: until the vet presses "Opprett journal" the journal has no id,
    // per-field autosave is a no-op (input.js keeps id-less edits memory-only) and the
    // text lives ONLY in the shared newJournal observable. Mirror it to localStorage,
    // debounced like the field autosave, so a drawer wipe / reload / tab crash can
    // never destroy the only copy. journalDraft ignores id-bearing journals.
    //
    // Mirroring is SUSPENDED from createNewJournal() until restoreNewJournalDraft():
    // drawer init stamps sale/animal/date (and calendar prefill) onto an empty shared
    // journal across several awaits — an unsuspended mirror firing in that window would
    // clear or overwrite the rescued draft it is about to restore (review P1). Only
    // real user edits happen outside that window.
    @action
    updateObjectKeyValue(obj, key, value) {
        super.updateObjectKeyValue(obj, key, value);
        if (obj === 'newJournal' && !this.draftMirrorSuspended && !this.newJournal?.id) {
            clearTimeout(this.draftMirrorTimer);
            this.draftMirrorTimer = setTimeout(() => {
                journalDraft.saveDraft(this.newJournal);
                // The draft now describes exactly this object — the identity used to
                // decide whether an insert may clear it (review P2).
                this.draftMirrorSource = this.newJournal;
            }, 750);
        }
    }

    // Flush a content-bearing, id-less newJournal to the localStorage draft NOW.
    // Called by every path that is about to blank or replace the shared observable —
    // a vet's typed-but-never-inserted text exists nowhere else (TICKET-01430).
    rescueNewJournalDraft() {
        clearTimeout(this.draftMirrorTimer);
        journalDraft.saveDraft(this.newJournal);
        // The rescued draft's journal object is about to be orphaned: no insert may
        // clear the draft until it has been restored into a new object.
        this.draftMirrorSource = null;
    }

    // Apply a mirrored draft back onto a freshly stamped, still id-less newJournal when
    // it belongs to the same sale+animal (both ids are globally unique, so this is safe
    // across clinics). Returns the draft's savedAt for the "restored" notice, or null.
    // The draft itself is kept until the journal is actually inserted. Also ends the
    // init mirror-suspension window — from here on, edits are the vet's.
    restoreNewJournalDraft() {
        this.draftMirrorSuspended = false;
        const { newJournal } = this;
        if (!newJournal || newJournal.id) {
            return null;
        }
        const draft = journalDraft.loadDraft({ sale: newJournal.sale, animal: newJournal.animal });
        if (!draft) {
            return null;
        }
        Object.keys(draft.fields).forEach(key => {
            this.updateObjectKeyValue('newJournal', key, draft.fields[key]);
        });
        this.draftMirrorSource = this.newJournal;
        return draft.savedAt || null;
    }

    createNewJournal() {
        this.rescueNewJournalDraft();
        this.draftMirrorSuspended = true;
        this.cleanupAi();
        this.updateKeyValue('newJournal', {
            members: [],
            changes: [],
            views: [],
            prescriptions: [],
            diagnoses: [],
            referrals: [],
        });
    }

    @action
    removeArrayObjectLocal({ field, id, md5, uuidv4, objectName }) {
        // Call parent implementation
        super.removeArrayObjectLocal({ field, id, md5, uuidv4, objectName });

        // Also update animalJournals if it exists
        const widx = this.animalJournals ? this.animalJournals.findIndex(e => e.id === id) : -1;
        if (widx > -1) {
            let idx = this.animalJournals[widx][field]?.findIndex(e => e.md5 === md5);
            if (!md5) {
                idx = this.animalJournals[widx][field]?.findIndex(e => e.uuidv4 === uuidv4);
            }
            if (idx > -1) {
                this.animalJournals[widx][field].splice(idx, 1);
            }
        }
    }

    @action
    editArrayObjectLocal({ field, key, value, id, md5, uuidv4, objectName }) {
        // Call parent implementation
        super.editArrayObjectLocal({ field, key, value, id, md5, uuidv4, objectName });

        // Also update animalJournals if it exists
        const widx = this.animalJournals ? this.animalJournals.findIndex(e => e.id === id) : -1;
        if (widx > -1) {
            let idx = this.animalJournals[widx][field]?.findIndex(e => e.md5 === md5);
            if (!md5) {
                idx = this.animalJournals[widx][field]?.findIndex(e => e.uuidv4 === uuidv4);
            }
            if (idx > -1) {
                this.animalJournals[widx][field][idx][key] = value;
            }
        }
    }

    getVeterinary(id) {
        return this.veterinaries.find(vet => vet.id === id);
    }

    async loadVeterinaries() {
        const response = await util.fetchApi(`/api/users/veterinaries/`, { method: 'GET' }, {});
        switch (response.status) {
            case 200:
                this.updateKeyValue('veterinaries', response.data);
                return response;

        }
    }

    async loadAnimalSpeciesList() {
        const response = await util.fetchApi(`/api/animalspecies/`, { method: 'GET' }, { limit: 200, sort: 'name' });
        if (response.status === 200) {
            this.updateKeyValue('animalSpeciesList', response.data || []);
        }
        return response;
    }

    async sendJournalEmail(ids, toEmail, includeReceipt, subject, body, includeImages, includeFiles, saleIds, includeBodyText = true, attachmentUrls = '', excludeFileUrls = '', idexxResultIds = '', includeFileUrls = '') {
        const idParams = ids.map(id => `id=${id}`).join('&');
        const queryParams = [
            idParams,
            `toEmail=${encodeURIComponent(toEmail)}`,
            `includeReceipt=${includeReceipt}`,
            `subject=${encodeURIComponent(subject)}`,
            `body=${encodeURIComponent(body)}`,
            `includeImages=${includeImages}`,
            `includeFiles=${includeFiles}`,
            `saleIds=${saleIds}`,
            `includeBodyText=${includeBodyText}`,
            `attachmentUrls=${encodeURIComponent(attachmentUrls)}`,
            `excludeFileUrls=${encodeURIComponent(excludeFileUrls)}`,
            `idexxResultIds=${encodeURIComponent(idexxResultIds)}`,
            `includeFileUrls=${encodeURIComponent(includeFileUrls)}`,
        ].join('&');
        const response = await util.fetchApi(
            `/api/journals/email/?${queryParams}`,
            { method: 'GET' },
            {},
        );
        switch (response.status) {
            case 200:
                return response;
        }
    }

    async save(...args) {
        const response = await super.save(...args);
        if (response && response.status < 300) {
            const journalId = args[0]?.id || response?.data?.id || null;
            firebaseSync.broadcastJournalUpdate(journalId);
        }
        return response;
    }

    async insert(...args) {
        const data = args[0];
        // TICKET-01239 double-submit guard. A fast double-click / double-tap on
        // "Opprett journal" fired createJournal -> saveJournal twice before
        // newJournal.id was set, so the SAME unsaved journal was POSTed twice and two
        // journals with identical content were created on one besøk (the prescription
        // then showed up on both). The manual new-journal flows insert the single
        // `this.newJournal` observable; batch/product flows build their own objects and
        // are left untouched. While an insert of this exact, still-id-less object is on
        // the wire, hand the second caller the same POST instead of firing another one.
        // Mirrors the sale insert guard (TICKET-01118).
        const guardNewJournal = !!data && data === this.newJournal && !data.id;
        if (guardNewJournal && this._newJournalInsertPromise && this._newJournalInsertObject === data) {
            return this._newJournalInsertPromise;
        }
        // NOTE: super.insert() is called directly here (not inside an arrow-function
        // IIFE). Babel's arrow-function transform cannot compile `super.prop` inside an
        // arrow without @babel/plugin-transform-classes, so the broadcast side-effect is
        // attached via .then() — an arrow that contains no `super` reference — instead.
        const promise = super.insert(...args).then(response => {
            if (response && response.status < 300) {
                const journalId = response?.data?.id || null;
                firebaseSync.broadcastJournalUpdate(journalId);
                // TICKET-01430: clear the mirrored draft ONLY when the inserted object
                // is the exact object the draft was mirroring. "Ny resept"/autoCreate
                // also insert the shared newJournal for the same sale+animal, but as a
                // fresh post-rescue object (draftMirrorSource is null then) — clearing
                // by pair alone would delete a rescued narrative draft (review P2).
                if (data && data === this.draftMirrorSource) {
                    journalDraft.clearDraft({ sale: data.sale, animal: data.animal });
                    this.draftMirrorSource = null;
                }
            }
            return response;
        });
        if (!guardNewJournal) {
            return promise;
        }
        this._newJournalInsertPromise = promise;
        this._newJournalInsertObject = data;
        try {
            return await promise;
        } finally {
            if (this._newJournalInsertObject === data) {
                this._newJournalInsertPromise = null;
                this._newJournalInsertObject = null;
            }
        }
    }

    async reopen(id, reason) {
        // TICKET-01342: reopening goes through the admin-gated reopen route
        // (mandatory reason + audit trail). The old PATCH closed:'null' path is
        // stripped server-side and must not be used.
        const response = await util.fetchApi(`/api/journals/reopen/`, { method: 'POST' }, {
            id,
            reason,
        });
        switch (response.status) {
            case 202:
                firebaseSync.broadcastJournalUpdate(id);
                return response;
        }
        return response;
    }

    async deleteJournal(id, reason) {
        // TICKET-01359: deleting always goes through the audited delete route
        // (mandatory reason + audit trail + sale-embed cleanup).
        const response = await util.fetchApi(`/api/journals/${id}`, { method: 'DELETE' }, {
            reason,
        });
        switch (response.status) {
            case 202:
                firebaseSync.broadcastJournalUpdate(id);
                return response;
        }
        return response;
    }

    async undeleteJournal(id) {
        const response = await util.fetchApi(`/api/journals/undelete/`, { method: 'POST' }, {
            id,
        });
        switch (response.status) {
            case 202:
                firebaseSync.broadcastJournalUpdate(id);
                return response;
        }
        return response;
    }

    async loadMedicineUsage() {
        const response = await util.fetchApi(`/api/journals/medicine-usage/`, { method: 'GET' }, {
            cutoffDate: this.cutoffDate,
            cutoffDateEnd: this.cutoffDateEnd,
        });
        switch (response.status) {
            case 200:
                this.updateKeyValue('medicineUsage', response.data);
                return response;
        }
    }

    async bulkClose(ids) {
        const response = await util.fetchApi(`/api/journals/bulk-close/`, { method: 'POST' }, {
            ids,
        });
        switch (response.status) {
            case 200:
                firebaseSync.broadcastJournalUpdate();
                this.loadAllJournals();
                return response;
        }
    }

    @action
    async reportDiagnosisToDyreId(journalId, diagnosisUuidv4) {
        this.updateKeyValue('reportingToDyreId', true);
        this.updateKeyValue('dyreIdReportError', null);
        try {
            const response = await util.fetchApi('/api/dyreid/post-diagnosis', { method: 'POST' }, {
                journalId,
                diagnosisUuidv4,
            });
            this.updateKeyValue('reportingToDyreId', false);
            if (response.status === 200 && response.data?.isSuccess) {
                return response.data;
            }
            this.updateKeyValue('dyreIdReportError', response.message || response.data?.errorMessage || 'Unknown error');
            return null;
        } catch (err) {
            this.updateKeyValue('reportingToDyreId', false);
            this.updateKeyValue('dyreIdReportError', err.message || 'Network error');
            return null;
        }
    }

    @action
    async reportAllDiagnosesToDyreId(saleId, journalId) {
        this.updateKeyValue('reportingBatchToDyreId', true);
        this.updateKeyValue('dyreIdBatchReportError', null);
        this.updateKeyValue('dyreIdBatchResults', null);
        try {
            const response = await util.fetchApi('/api/dyreid/post-diagnosis-batch', { method: 'POST' }, {
                saleId,
                ...(journalId > 0 ? { journalId } : {}),
            });
            this.updateKeyValue('reportingBatchToDyreId', false);
            if (response.status === 200) {
                this.updateKeyValue('dyreIdBatchResults', response.data);
                return response.data;
            }
            this.updateKeyValue('dyreIdBatchReportError', response.message || 'Unknown error');
            return null;
        } catch (err) {
            this.updateKeyValue('reportingBatchToDyreId', false);
            this.updateKeyValue('dyreIdBatchReportError', err.message || 'Network error');
            return null;
        }
    }

    async bulkCloseByDate(cutoffDate, dryRun = false) {
        const response = await util.fetchApi(`/api/journals/bulk-close-by-date/`, { method: 'POST' }, {
            cutoffDate,
            dryRun,
        });
        return response;
    }

}

Object.assign(JournalStore.prototype, partnerStoreMethods);

const store = new JournalStore();
export default store;
